import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ResponseCard from '@/components/base/ResponseCard';
import { cases } from '@/mocks/cases';
import { siteMeta } from '@/mocks/site';
import type { CaseItem } from '@/types/content';

const caseList = cases as unknown as CaseItem[];
const stances = ['全部', '否认', '承认', '律师函', '澄清', '未回应'] as const;

export default function ResponsesPage() {
  const [active, setActive] = useState<string>('全部');

  const all = useMemo(
    () =>
      caseList.flatMap((item) =>
        item.responses.map((res) => ({ res, caseTitle: item.title, caseId: item.id }))
      ),
    []
  );

  const filtered = all.filter((entry) => active === '全部' || entry.res.stance === active);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '当事人回应合集',
        url: `${siteMeta.domain}/responses`,
        description: '收录各方对相关事件的公开表态，标注立场与来源，区分已澄清与尚未回应。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
          { '@type': 'ListItem', position: 2, name: '当事人回应', item: `${siteMeta.domain}/responses` },
        ],
      },
    ],
    []
  );

  return (
    <PageLayout>
      <SeoHead
        title="当事人回应合集 | 立场与来源全收录 · 911吃瓜"
        description="911吃瓜收录当事人对相关事件的公开回应，标注否认、承认、律师函、澄清与未回应等立场，并说明来源与时间，方便你判断信息的可靠程度。"
        keywords="当事人回应,公开声明,立场梳理,回应合集,911吃瓜"
        path="/responses"
        jsonLd={jsonLd}
      />

      <section className="w-full bg-background-100 border-b border-background-200 pt-24 md:pt-28 pb-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumbs items={[{ label: '当事人回应' }]} />
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">当事人回应合集</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base leading-7 text-foreground-600">
            每一条回应都写明了立场与来源。我们只做记录，不替任何人下结论，也不把尚未回应当作默认立场。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap items-center gap-2">
          {stances.map((stance) => (
            <button
              key={stance}
              type="button"
              onClick={() => setActive(stance)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                active === stance
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {stance}
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共收录 <span className="font-semibold text-primary-600">{filtered.length}</span> 条回应
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((entry, index) => (
              <div key={`${entry.caseId}-${entry.res.personId}-${index}`} className="flex flex-col">
                <ResponseCard item={entry.res} caseTitle={entry.caseTitle} />
                <Link
                  to={`/case/${entry.caseId}`}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 self-start"
                >
                  查看事件完整梳理
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 text-center py-16 bg-background-100 border border-background-200 rounded-lg">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-chat-off-line" />
            </span>
            <p className="mt-4 font-heading font-bold text-foreground-900">该立场下暂无回应</p>
            <p className="mt-2 text-sm text-foreground-500">切换其他立场标签看看。</p>
          </div>
        )}
      </section>
    </PageLayout>
  );
}